import { Box, Button, Flex, Skeleton, SkeletonText, Stack, Tabs, Text } from '@chakra-ui/react';
import { CalendarBlank, CaretLeft, Clock, Plus } from '@phosphor-icons/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { fetchAPI } from '@/api/fetch';
import { apiGetMeeting, apiUpdateJobCandidateFollowupQuestions } from '@/api/services';
import { CandidateWithJobContext } from '@/types/Candidate';
import { AlertsAndFollowUpQuestion } from '@/types/AnalysisResult';
import { Job } from '@/types/Job';
import { Meeting, Transcript } from '@/types/Meeting';
import {
  CandidateNoteSection,
  FollowUpQuestionStatus,
  isMeetingCanAddFollowUpItem,
  MeetingStatus
} from '@/types/enums';

import FollowUpItemBox from '@/views/Candidate/components/FollowUpItemBox';
import RecordingIndicator from '@/components/media/RecordingIndicator';
import Resume from '@/components/data-display/Resume';
import TranscriptList from './TranscriptList';
import InterviewNote from './InterviewNote';
import UpdateMeetingLinkDialog from '../UpdateMeetingLinkDialog';
import { convertMeetingToMeetingSummary } from '@/utils/meeting';
import { getCandidateFullName } from '@/utils';

const Breadcrumb = ({
  meeting,
  candidate,
  meetingId
}: {
  meeting: Meeting | null;
  candidate: CandidateWithJobContext | null;
  meetingId: string | undefined;
}) => {
  const navigate = useNavigate();

  if (!meeting) {
    return (
      <Flex mb="16px">
        <SkeletonText noOfLines={1} h="8" w="50%" />
      </Flex>
    );
  }

  return (
    <Flex direction="row" align="center" gap="16px" mb="16px">
      <Box
        as="button"
        onClick={() => navigate('/interview')}
        cursor="pointer"
        aria-label="Go back to interviews"
        _hover={{ opacity: 0.8 }}
        transition="opacity 0.2s"
      >
        <CaretLeft size={28} color="#545454" />
      </Box>
      <Box fontSize="18px" color="neutrals.700">
        <Text
          as="span"
          cursor="pointer"
          onClick={() => navigate('/interview')}
          _hover={{ textDecoration: 'underline' }}
        >
          Interview
        </Text>
        <Text as="span" mx="4px">
          {' '}
          /{' '}
        </Text>
        <Text
          as="b"
          cursor="pointer"
          onClick={() => navigate(`/interview/${meetingId}`)}
          _hover={{ textDecoration: 'underline' }}
        >
          {getCandidateFullName(candidate?.first_name ?? '', candidate?.last_name ?? '')} -{' '}
          {meeting.title}
        </Text>
      </Box>
    </Flex>
  );
};

const RecordingBox = ({ duration }: { duration: string }) => (
  <Box p="4px 12px" bg="neutrals.50" borderRadius="8px" w="fit-content" whiteSpace="nowrap">
    <Flex align="center" gap="8px">
      <RecordingIndicator />
      <Text fontSize="16px" color="neutrals.700" fontWeight="semibold">
        Recording {duration}
      </Text>
    </Flex>
  </Box>
);

const MeetingInfo = ({
  meeting,
  candidate
}: {
  meeting: Meeting;
  candidate: CandidateWithJobContext | null;
}) => {
  const getLastWorkExperience = useCallback(() => {
    return candidate?.resume_data?.work_experiences[0] || null;
  }, [candidate]);

  const getMeetingTime = useCallback(() => {
    return new Date(meeting.meeting_start_time * 1000);
  }, [meeting.meeting_start_time]);

  const getMeetingDuration = useCallback(() => {
    return Math.round((meeting.timestamp.ended_at - meeting.timestamp.recorded_at) / 60);
  }, [meeting.timestamp.ended_at, meeting.timestamp.recorded_at]);

  const lastWorkExperience = useMemo(() => getLastWorkExperience(), [getLastWorkExperience]);
  const meetingTime = useMemo(() => getMeetingTime(), [getMeetingTime]);
  const meetingDuration = useMemo(() => getMeetingDuration(), [getMeetingDuration]);

  return (
    <Stack
      w="100%"
      fontSize="16px"
      mb="16px"
      color="neutrals.700"
      overflow="auto"
      whiteSpace="nowrap"
    >
      <Text>
        {lastWorkExperience?.job_role || 'N/A'} @ {lastWorkExperience?.company_name || 'N/A'}
      </Text>
      <Flex gap="8px" align="center">
        <CalendarBlank size={20} color="#6B7280" aria-hidden="true" />
        {meetingTime.toLocaleDateString('default', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        })}
        <Text>·</Text>
        <Clock size={20} color="#6B7280" aria-hidden="true" />
        {meetingTime.toLocaleTimeString('default', {
          hour: 'numeric',
          minute: 'numeric'
        })}
        {meeting.status === MeetingStatus.Done && (
          <>
            <Text>·</Text>
            <Text>{meetingDuration} mins</Text>
          </>
        )}
      </Flex>
    </Stack>
  );
};

const FollowUpItems = ({
  canAddFollowUpItem = false,
  followUpItems,
  onUpdateItem,
  onAddItem
}: {
  canAddFollowUpItem?: boolean;
  followUpItems: AlertsAndFollowUpQuestion[];
  onUpdateItem: (index: number, item: AlertsAndFollowUpQuestion | null) => Promise<boolean>;
  onAddItem: () => void;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const openItems = useMemo(() => {
    return followUpItems.filter((item) => item.status === FollowUpQuestionStatus.Pending);
  }, [followUpItems]);
  const closedItems = useMemo(() => {
    return followUpItems.filter((item) => item.status !== FollowUpQuestionStatus.Pending);
  }, [followUpItems]);

  const handleAddItem = () => {
    onAddItem();

    setTimeout(() => {
      if (containerRef.current) {
        containerRef.current.scrollTop = containerRef.current.scrollHeight;
      }
    }, 100);
  };

  return (
    <Flex
      direction="column"
      justify="flex-start"
      align="flex-start"
      h="100%"
      overflowY="auto"
      ref={containerRef}
    >
      <Flex
        justify="space-between"
        align="center"
        w="100%"
        mb={openItems.length === 0 ? '12px' : '24px'}
      >
        <Text fontSize="24px" fontWeight="bold">
          Follow Up Items
        </Text>
        {canAddFollowUpItem && (
          <Button variant="ghost" color="neutrals.800" p="0" onClick={handleAddItem}>
            <Plus size={24} weight="bold" />
          </Button>
        )}
      </Flex>
      <Stack gap="24px" w="100%" mb={openItems.length === 0 ? '0px' : '24px'}>
        {openItems.map((item) => {
          const originalIndex = followUpItems.findIndex((i) => i === item);
          return (
            <FollowUpItemBox
              key={originalIndex}
              followUpItem={item}
              index={originalIndex}
              onUpdateItem={onUpdateItem}
              readonly={!canAddFollowUpItem}
              evaluate
              withStatus
            />
          );
        })}
        {closedItems.map((item) => {
          const originalIndex = followUpItems.findIndex((i) => i === item);
          return (
            <FollowUpItemBox
              key={originalIndex}
              followUpItem={item}
              index={originalIndex}
              onUpdateItem={onUpdateItem}
              readonly={!canAddFollowUpItem}
              evaluate
              withStatus
              collapsible
            />
          );
        })}
      </Stack>
    </Flex>
  );
};

const TabContent = ({
  tab,
  meetingId,
  candidate,
  transcripts,
  isRecording,
  showImportMockDialog = false
}: {
  tab: string;
  meetingId: string;
  candidate: CandidateWithJobContext | null;
  transcripts: Transcript[];
  isRecording: boolean;
  showImportMockDialog?: boolean;
}) => {
  if (!candidate) return null;

  switch (tab) {
    case 'transcript':
      return (
        <Box h="calc(100vh - 360px)" overflowY="auto">
          <TranscriptList
            meetingId={meetingId}
            transcripts={transcripts}
            scrollToBottom={isRecording}
            showImportMockDialog={showImportMockDialog}
          />
        </Box>
      );
    case 'resume':
      return (
        <Box h="calc(100vh - 360px)" overflowY="auto">
          <Resume resume={candidate.resume_data} />
        </Box>
      );
    case 'note':
      return (
        <Box h="calc(100vh - 360px)" overflowY="auto">
          <InterviewNote
            meetingId={meetingId}
            initialNote={
              candidate.notes.find((note) => note.section === CandidateNoteSection.Interview)
                ?.note || ''
            }
          />
        </Box>
      );
    default:
      return null;
  }
};

export default function InterviewDetails() {
  const { meetingId } = useParams<{ meetingId: string }>();
  const navigate = useNavigate();
  const [tab, setTab] = useState('transcript');
  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [candidate, setCandidate] = useState<CandidateWithJobContext | null>(null);
  const [job, setJob] = useState<Job | null>(null);
  const [followUpItems, setFollowUpItems] = useState<AlertsAndFollowUpQuestion[]>([]);
  const [isRecording, setIsRecording] = useState(true);
  const [isMeetingStarted, setIsMeetingStarted] = useState(false);
  const [transcripts, setTranscripts] = useState<Transcript[]>([]);
  const [liveRecordingDuration, setLiveRecordingDuration] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isUpdateLinkDialogOpen, setIsUpdateLinkDialogOpen] = useState(false);

  const checkMeetingStarted = useCallback((meeting_time: number, status: MeetingStatus) => {
    const currentTimestamp = Math.floor(Date.now() / 1000);
    const isMeetingTimeReached = meeting_time <= currentTimestamp;
    const isMeetingInInitialState = [
      MeetingStatus.Created,
      MeetingStatus.JoiningCall,
      MeetingStatus.InWaitingRoom
    ].includes(status);

    return isMeetingTimeReached && isMeetingInInitialState;
  }, []);

  const fetchMeetingDetails = useCallback(async () => {
    if (!meetingId) {
      navigate('/interview');
      return;
    }

    try {
      setIsLoading(true);
      const response = await fetchAPI<Meeting>({
        request: () => apiGetMeeting({ meetingId })
      });

      if (response.success) {
        const { data } = response;
        setMeeting(data);
        setCandidate(data.candidate || null);
        setJob(data.job || null);
        setTranscripts(data.transcripts || []);
        setFollowUpItems(data.candidate?.alerts_and_follow_up_questions || []);
        setIsRecording(
          [MeetingStatus.InCallRecording, MeetingStatus.InCallNotRecording].includes(data.status)
        );
        setIsMeetingStarted(checkMeetingStarted(data.meeting_start_time, data.status));
        if (data.status === MeetingStatus.Fatal) {
          setIsUpdateLinkDialogOpen(true);
        }
        setError(null);
      } else {
        const errorMessage = response.message || 'Failed to fetch meeting details';
        setError(errorMessage);
        console.error('Error fetching meeting details:', errorMessage);
        navigate('/interview');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      setError(errorMessage);
      console.error('Error fetching meeting details:', errorMessage);
      navigate('/interview');
    } finally {
      setIsLoading(false);
    }
  }, [checkMeetingStarted, meetingId, navigate]);

  useEffect(() => {
    fetchMeetingDetails();
  }, [fetchMeetingDetails]);

  useEffect(() => {
    if (!isRecording && !isMeetingStarted) return;

    const refreshMeetingDetails = async () => {
      if (!meetingId) return;

      try {
        const response = await fetchAPI<Meeting>({
          request: () => apiGetMeeting({ meetingId, updateTranscript: true })
        });

        if (response.success) {
          const { data } = response;
          setMeeting(data);
          setTranscripts(data.transcripts || []);
          setIsRecording(
            [MeetingStatus.InCallRecording, MeetingStatus.InCallNotRecording].includes(data.status)
          );
          setIsMeetingStarted(checkMeetingStarted(data.meeting_start_time, data.status));
        } else {
          console.error('Error fetching meeting details:', response.message);
        }
      } catch (error) {
        console.error('Error fetching meeting details:', error);
      }
    };

    const intervalId = setInterval(refreshMeetingDetails, 5000);
    return () => clearInterval(intervalId);
  }, [isRecording, isMeetingStarted, meetingId, checkMeetingStarted]);

  useEffect(() => {
    if (!isRecording || !meeting) return;

    const updateLiveRecordingDuration = () => {
      let recordedAt = meeting.timestamp.recorded_at;
      if (recordedAt === 0) {
        recordedAt = meeting.timestamp.joined_at;
      }
      if (recordedAt === 0) {
        setLiveRecordingDuration('0:00');
        return;
      }
      const duration = Math.max(0, Math.floor(Date.now() / 1000) - recordedAt);
      const minutes = Math.floor(duration / 60);
      const seconds = Math.floor(duration % 60);
      setLiveRecordingDuration(`${minutes}:${seconds.toString().padStart(2, '0')}`);
    };

    updateLiveRecordingDuration();
    const intervalId = setInterval(updateLiveRecordingDuration, 1000);
    return () => clearInterval(intervalId);
  }, [isRecording, meeting]);

  const updateFollowUpItem = async (
    index: number,
    item: AlertsAndFollowUpQuestion | null
  ): Promise<boolean> => {
    if (!meetingId || !candidate || !job) return false;

    const updatedItems = [...followUpItems];

    if (item === null) {
      const removedItem = updatedItems[index];
      updatedItems.splice(index, 1);

      if (removedItem.title === '') {
        setFollowUpItems(updatedItems);
        return true;
      }
    } else {
      updatedItems[index] = item;
    }

    try {
      const response = await fetchAPI<void>({
        request: () =>
          apiUpdateJobCandidateFollowupQuestions({
            jobId: job.job_id,
            candidateId: candidate.candidate_id,
            payload: {
              followup_questions: updatedItems
            }
          })
      });

      if (response.success) {
        setFollowUpItems(updatedItems);
      } else {
        console.error('Failed to update follow-up questions:', response.message);
      }

      return response.success;
    } catch (error) {
      console.error('An error occurred while updating follow-up questions:', error);
      throw error;
    }
  };

  const addFollowUpItem = useCallback(() => {
    setFollowUpItems((prevItems) => [
      ...prevItems,
      {
        title: '',
        alert: '',
        follow_up_questions: [{ question: '', question_type: 'essay_question' }],
        status: FollowUpQuestionStatus.Pending
      }
    ]);
  }, []);

  const onEditSuccess = () => {
    fetchMeetingDetails();
    setIsUpdateLinkDialogOpen(false);
  };

  if (error) {
    return (
      <Flex w="100%" h="100vh" align="center" justify="center">
        <Text color="error.700">{error}</Text>
      </Flex>
    );
  }

  return (
    <Flex w="100%" flexDir="column" p="40px 32px">
      <Breadcrumb meeting={meeting} candidate={candidate} meetingId={meetingId} />

      <Flex direction="row" justify="flex-start" align="flex-start" gap="24px" h="100%">
        <Box
          flex={2}
          h="100%"
          w="100%"
          p="32px"
          bg="background.secondary"
          border="1px solid"
          borderColor="neutrals.200"
          borderRadius="12px"
        >
          {isLoading ? (
            <Stack w="100%" gap="24px">
              <SkeletonText noOfLines={3} w="80%" gap="4" />
              <Skeleton borderRadius="8px" h="120px" w="100%" />
              <Skeleton borderRadius="8px" h="80px" w="100%" />
            </Stack>
          ) : meeting ? (
            <Flex direction="column" align="flex-start" h="100%">
              <Stack w="100%" gap="4px">
                <Text fontSize="16px" fontWeight="semibold" color="neutrals.700">
                  {job?.client.name} - {job?.job_title}
                </Text>
                <Flex justify="space-between" align="center" w="100%">
                  <Text
                    fontSize="28px"
                    fontWeight="bold"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                    maxW={isRecording ? 'calc(100% - 170px)' : '100%'}
                  >
                    {candidate?.first_name} - {meeting.title}
                  </Text>
                  {isRecording && <RecordingBox duration={liveRecordingDuration} />}
                </Flex>
                <MeetingInfo meeting={meeting} candidate={candidate} />
                <Tabs.Root value={tab} onValueChange={(e) => setTab(e.value)} colorPalette="blue">
                  <Box position="relative">
                    <Tabs.List w="100%">
                      <Tabs.Trigger value="transcript" asChild>
                        <Box
                          fontSize="16px"
                          color={tab === 'transcript' ? 'primary.1000' : 'neutrals.900'}
                        >
                          Transcript
                        </Box>
                      </Tabs.Trigger>
                      <Tabs.Trigger value="resume" asChild>
                        <Box
                          fontSize="16px"
                          color={tab === 'resume' ? 'primary.1000' : 'neutrals.900'}
                        >
                          Resume
                        </Box>
                      </Tabs.Trigger>
                      <Tabs.Trigger value="note" asChild>
                        <Box
                          fontSize="16px"
                          color={tab === 'note' ? 'primary.1000' : 'neutrals.900'}
                        >
                          Note
                        </Box>
                      </Tabs.Trigger>
                    </Tabs.List>
                    <Button
                      position="absolute"
                      right="0"
                      top="50%"
                      transform="translateY(-50%)"
                      p="0"
                      variant="ghost"
                      color="neutrals.700"
                      fontSize="16px"
                      fontWeight="semibold"
                      _hover={{ bg: 'transparent' }}
                      onClick={() =>
                        navigate(`/job/details/${job?.job_id}/candidate/${candidate?.candidate_id}`)
                      }
                    >
                      View Candidate Profile
                    </Button>
                  </Box>
                  <Tabs.Content value="transcript">
                    <TabContent
                      tab="transcript"
                      meetingId={meetingId!}
                      candidate={candidate}
                      transcripts={transcripts}
                      isRecording={isRecording}
                      showImportMockDialog={meeting.status === MeetingStatus.Created}
                    />
                  </Tabs.Content>
                  <Tabs.Content value="resume">
                    <TabContent
                      tab="resume"
                      meetingId={meetingId!}
                      candidate={candidate}
                      transcripts={transcripts}
                      isRecording={isRecording}
                    />
                  </Tabs.Content>
                  <Tabs.Content value="note">
                    <TabContent
                      tab="note"
                      meetingId={meetingId!}
                      candidate={candidate}
                      transcripts={transcripts}
                      isRecording={isRecording}
                    />
                  </Tabs.Content>
                </Tabs.Root>
              </Stack>
            </Flex>
          ) : null}
        </Box>
        <Box
          flex={1}
          h="calc(100vh - 100px)"
          w="100%"
          p="32px"
          bg="background.secondary"
          border="1px solid"
          borderColor="neutrals.200"
          borderRadius="12px"
        >
          {isLoading ? (
            <Stack w="100%" gap="24px">
              <SkeletonText noOfLines={3} w="80%" />
              <Skeleton borderRadius="8px" h="120px" w="100%" />
              <Skeleton borderRadius="8px" h="80px" w="100%" />
              <Skeleton borderRadius="8px" h="120px" w="100%" />
              <Skeleton borderRadius="8px" h="80px" w="100%" />
            </Stack>
          ) : meeting ? (
            <FollowUpItems
              canAddFollowUpItem={isMeetingCanAddFollowUpItem(meeting.status)}
              followUpItems={followUpItems}
              onUpdateItem={updateFollowUpItem}
              onAddItem={addFollowUpItem}
            />
          ) : null}
        </Box>
      </Flex>
      {meeting && (
        <UpdateMeetingLinkDialog
          meeting={convertMeetingToMeetingSummary(meeting)}
          onEditMeeting={onEditSuccess}
          isOpen={isUpdateLinkDialogOpen}
          onClose={() => setIsUpdateLinkDialogOpen(false)}
          disableClose
        />
      )}
    </Flex>
  );
}
